/** @jsxImportSource @emotion/react */
import {
  Balances,
  EmptyContent,
  useSession,
} from '@quickbit/backoffice-core-frontend';
import {
  Button,
  Card,
  createStyles,
  Icon,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom'; // useNavigate
import { useAsync } from 'react-use';
import { useGetCorporateWalletTransactions } from 'api/walletApi';
import { QBLoader } from 'components';
import { useOrganisation } from 'contexts';
import { getConfig } from 'tabs/api';
import { ExportCsv } from './components/ExportCsv';
import { Transactions } from './components/Transactions';
import { WalletNotAvailable } from './components/WalletNotAvailable';
import { useSetSendData } from './Send/context';

export const Wallet = () => {
  const { sizing, spacing, palette } = useTheme();
  const navigate = useNavigate();
  const setSendData = useSetSendData();
  const session = useSession();
  const {
    selectedMerchant: { merchantId },
    isPlatform,
  } = useOrganisation();
  const {
    data: transactionsObject,
    isLoading,
    error,
  } = useGetCorporateWalletTransactions(merchantId);
  const styles = useStyles();

  useEffect(() => {
    if (merchantId && transactionsObject) {
      setSendData({
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        walletBalance: transactionsObject.balances?.usdc?.usdc,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        walletBalanceReference: transactionsObject.balances?.usdc?.eur,
        merchantId,
      });
    }
  }, [merchantId, transactionsObject, setSendData]);

  if (isPlatform) {
    throw new Error('Platform users do not have access to the wallet');
  }

  const { value: config } = useAsync(async () => {
    if (!merchantId) return;
    const configResponse = await getConfig();
    return configResponse.config;
  }, [merchantId]);

  if (isLoading) {
    return <QBLoader />;
  }

  if (config && !config.allowCorporateWallet) return <WalletNotAvailable />;

  const transactions = transactionsObject?.transactions.sort((a, b) =>
    new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1
  );

  const isAuthorizedToSendCrypto =
    session.authenticated &&
    session.permissions.includes('edit_corporate_wallet');

  return (
    <>
      <div css={{ maxWidth: sizing.large }}>
        <Card
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing.s,
          }}
        >
          <Typography variant="subtitle">Wallet</Typography>
          {merchantId && transactions && (
            <ExportCsv
              merchantId={merchantId}
              disabled={transactions.length === 0}
            />
          )}
        </Card>
        <div
          css={{
            marginBottom: spacing.xs,
            display: 'flex',
            gap: spacing.xxxs,
            flexDirection: 'column',
          }}
        >
          <Typography variant="subtitle">Balances</Typography>
          {transactionsObject && (
            <div css={styles.wrappingContainer}>
              <Balances
                balances={transactionsObject.balances}
                addresses={transactionsObject.addresses}
              />
            </div>
          )}
          {error && (
            <EmptyContent
              heading="Oops! Something went wrong."
              body="Go back"
              action={
                <Button
                  text="Return"
                  variant="primary"
                  onClick={() => navigate(`/Home`)}
                />
              }
            />
          )}
        </div>
        <Card
          css={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: spacing.s,
          }}
        >
          {isAuthorizedToSendCrypto ? (
            <div
              onClick={() => navigate('USDC/details')}
              css={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                cursor: 'pointer',
              }}
            >
              <Icon name="send" />
              <Typography variant="bodySmall">Send</Typography>
            </div>
          ) : (
            <div>
              You do not have the required permissions to send cryptocurrency.
              Please check your account settings or contact support for
              assistance.
            </div>
          )}
        </Card>
        {error && (
          <Card
            css={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: 4,
            }}
          >
            <Typography variant="bodyLarge" color={palette.danger.default}>
              Failed to fetch USDT transactions.
            </Typography>
          </Card>
        )}
        {transactions && transactions.length > 0 && merchantId && (
          <div>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Transactions
                merchantId={merchantId}
                transactions={transactions}
              />
            </div>
          </div>
        )}
      </div>
      <Outlet />
    </>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  wrappingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
}));
